import * as THREE from 'three';

/**
 * ===========================================================================
 *  BEAMS — light shafts, lightning arcs and pillars
 * ===========================================================================
 *
 * A beam is a quad stretched between two world points that rolls about that
 * axis to face the camera. One instanced draw call covers every shaft of the
 * musou aura, every lightning arc and every ground-to-sky pillar.
 *
 * Styles (aB.x)
 *   0 SHAFT     smooth taper, soft edges, brightest at the source end
 *   1 BOLT      the axis is displaced by hashed noise that re-rolls at 30 Hz
 *   2 PILLAR    hard bright core with a wide soft skirt, no taper
 *   3 SPIKE     narrow at the far end, used for musou radial rays
 *
 * Per-instance attributes
 *   iP0 vec3  start                iP1 vec3  end
 *   iA  vec4  birth, life, width at t=0, width at t=1
 *   iB  vec4  style, seed, brightness, jitter amplitude
 *   iC0 ubyte4 rgb + alpha at t=0  iC1 ubyte4 rgb + alpha at t=1
 */

export const B = {
  x0: 0, y0: 0, z0: 0, x1: 0, y1: 1, z1: 0,
  life: 0.3, w0: 0.3, w1: 0.3, style: 0, bright: 1, jitter: 0,
  r0: 1, g0: 1, b0: 1, a0: 1, r1: 1, g1: 1, b1: 1, a1: 0,
};
export function resetB() {
  B.x0 = B.y0 = B.z0 = 0; B.x1 = 0; B.y1 = 1; B.z1 = 0;
  B.life = 0.3; B.w0 = 0.3; B.w1 = 0.3; B.style = 0; B.bright = 1; B.jitter = 0;
  B.r0 = B.g0 = B.b0 = 1; B.a0 = 1; B.r1 = B.g1 = B.b1 = 1; B.a1 = 0;
  return B;
}

const VERT = /* glsl */`
attribute vec3 iP0;
attribute vec3 iP1;
attribute vec4 iA;
attribute vec4 iB;
attribute vec4 iC0;
attribute vec4 iC1;

uniform float uTime;

varying vec2 vUv;
varying vec4 vCol;
varying float vStyle;

float h11(float p) { return fract(sin(p * 127.1) * 43758.5453); }

void main() {
  float age = uTime - iA.x;
  float t = age / max(iA.y, 1e-4);
  if (age < 0.0 || t >= 1.0) {
    vUv = vec2(0.0); vCol = vec4(0.0); vStyle = 0.0;
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    return;
  }

  float u = position.y + 0.5;                      // 0 at start .. 1 at end
  vec3 axis = iP1 - iP0;
  float len = length(axis);
  vec3 dir = len > 1e-5 ? axis / len : vec3(0.0, 1.0, 0.0);
  vec3 p = iP0 + axis * u;

  if (iB.x > 0.5 && iB.x < 1.5) {                  // BOLT: re-rolled kink
    float seg = floor(u * 9.0);
    float roll = floor(uTime * 30.0);
    vec3 rnd = vec3(h11(seg + iB.y * 37.0 + roll),
                    h11(seg * 3.7 + iB.y * 11.0 + roll * 1.3),
                    h11(seg * 7.1 + iB.y * 53.0 + roll * 2.1)) - 0.5;
    float env = sin(u * 3.14159);
    p += rnd * iB.w * len * env;
  }

  vec3 toCam = normalize(cameraPosition - p);
  vec3 side = cross(dir, toCam);
  float sl = length(side);
  side = sl > 1e-4 ? side / sl : vec3(1.0, 0.0, 0.0);

  float w = mix(iA.z, iA.w, u);
  if (iB.x > 2.5) w *= mix(1.0, 0.06, u * u);      // SPIKE
  p += side * (position.x * w);

  vUv = vec2(position.x + 0.5, u);
  float a = mix(iC0.a, iC1.a, t) * (1.0 - t) * (1.0 - t);
  vCol = vec4(mix(iC0.rgb, iC1.rgb, t) * iB.z, a);
  vStyle = iB.x;
  gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
}
`;

const FRAG = /* glsl */`
varying vec2 vUv;
varying vec4 vCol;
varying float vStyle;

void main() {
  float d = abs(vUv.x - 0.5) * 2.0;
  float core = exp(-d * d * 16.0);
  float skirt = exp(-d * d * 2.4);
  float prof = vStyle > 1.5 && vStyle < 2.5 ? (core * 0.9 + skirt * 0.45)
                                            : (core * 0.75 + skirt * 0.35);
  float along = 1.0;
  if (vStyle < 0.5) along = smoothstep(1.0, 0.55, vUv.y) * smoothstep(0.0, 0.06, vUv.y);
  else if (vStyle > 2.5) along = smoothstep(1.0, 0.20, vUv.y);
  else along = smoothstep(0.0, 0.05, vUv.y) * smoothstep(1.0, 0.94, vUv.y);
  float a = prof * along * vCol.a;
  if (a < 0.004) discard;
  gl_FragColor = vec4(vCol.rgb, a);
}
`;

const QUAD_POS = new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]);
const QUAD_IDX = new Uint16Array([0, 1, 2, 0, 2, 3]);

export class BeamPool {
  constructor(max = 96) {
    const n = this.max = Math.max(4, max | 0);
    this.head = 0; this.wrapped = false; this.maxExpiry = -1;
    this.expiry = new Float32Array(n);
    this.priority = new Uint8Array(n);
    this.spawned = 0; this.overwrites = 0; this.dropped = 0;
    this.p0 = new Float32Array(n * 3);
    this.p1 = new Float32Array(n * 3);
    this.a = new Float32Array(n * 4);
    this.b = new Float32Array(n * 4);
    this.c0 = new Uint8Array(n * 4);
    this.c1 = new Uint8Array(n * 4);
    this._lo = 0; this._hi = -1;

    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(QUAD_POS, 3));
    g.setIndex(new THREE.BufferAttribute(QUAD_IDX, 1));
    const dyn = (arr, size, norm = false) =>
      new THREE.InstancedBufferAttribute(arr, size, norm).setUsage(THREE.DynamicDrawUsage);
    this._attrs = [
      g.setAttribute('iP0', dyn(this.p0, 3)).attributes.iP0,
      g.setAttribute('iP1', dyn(this.p1, 3)).attributes.iP1,
      g.setAttribute('iA', dyn(this.a, 4)).attributes.iA,
      g.setAttribute('iB', dyn(this.b, 4)).attributes.iB,
      g.setAttribute('iC0', dyn(this.c0, 4, true)).attributes.iC0,
      g.setAttribute('iC1', dyn(this.c1, 4, true)).attributes.iC1,
    ];
    this._ranges = this._attrs.map(() => ({ start: 0, count: 0 }));
    g.instanceCount = 0;
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this.geometry = g;

    this.material = new THREE.ShaderMaterial({
      uniforms: { uTime: { value: 0 } },
      vertexShader: VERT, fragmentShader: FRAG,
      transparent: true, depthTest: true, depthWrite: false,
      blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
      toneMapped: false, fog: false,
    });
    this.material.name = 'vfxBeams';
    this.mesh = new THREE.Mesh(g, this.material);
    this.mesh.name = 'vfxBeams';
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.renderOrder = 11;
    this.mesh.castShadow = this.mesh.receiveShadow = false;
  }

  /** Low-priority aura shafts cannot evict a still-live hero/finisher beam. */
  push(now, priority = 1) {
    const start = this.head;
    let i = start;
    const pri = priority > 2 ? 2 : priority < 0 ? 0 : priority | 0;
    if (this.expiry[i] > now && this.priority[i] > pri) {
      i = -1;
      const probe = Math.min(12, this.max);
      for (let k = 1; k < probe; k++) {
        const j = (start + k) % this.max;
        if (this.expiry[j] <= now || this.priority[j] <= pri) { i = j; break; }
      }
      if (i < 0) { this.dropped++; return -1; }
    }
    if (this.expiry[i] > now) this.overwrites++;
    this.head = i + 1;
    if (this.head >= this.max) { this.head = 0; this.wrapped = true; }
    else if (i < start) this.wrapped = true;
    this.priority[i] = pri;
    this.spawned++;
    const i3 = i * 3, i4 = i * 4;
    this.p0[i3] = B.x0; this.p0[i3 + 1] = B.y0; this.p0[i3 + 2] = B.z0;
    this.p1[i3] = B.x1; this.p1[i3 + 1] = B.y1; this.p1[i3 + 2] = B.z1;
    this.a[i4] = now; this.a[i4 + 1] = B.life; this.a[i4 + 2] = B.w0; this.a[i4 + 3] = B.w1;
    this.b[i4] = B.style; this.b[i4 + 1] = (i * 0.6180339887) % 1;
    this.b[i4 + 2] = B.bright; this.b[i4 + 3] = B.jitter;
    this.c0[i4] = B.r0 * 255; this.c0[i4 + 1] = B.g0 * 255; this.c0[i4 + 2] = B.b0 * 255; this.c0[i4 + 3] = B.a0 * 255;
    this.c1[i4] = B.r1 * 255; this.c1[i4 + 1] = B.g1 * 255; this.c1[i4 + 2] = B.b1 * 255; this.c1[i4 + 3] = B.a1 * 255;
    const e = now + B.life;
    this.expiry[i] = e;
    if (e > this.maxExpiry) this.maxExpiry = e;
    if (this._hi < 0) { this._lo = i; this._hi = i; }
    else { if (i < this._lo) this._lo = i; if (i > this._hi) this._hi = i; }
    return i;
  }

  flush(now) {
    this.material.uniforms.uTime.value = now;
    if (this._hi < 0 && now > this.maxExpiry) { this.head = 0; this.wrapped = false; }
    if (this._hi >= 0) {
      const lo = this._lo, cnt = this._hi - this._lo + 1;
      for (let k = 0; k < this._attrs.length; k++) {
        const at = this._attrs[k], sz = at.itemSize, r = this._ranges[k];
        at.updateRanges.length = 0;
        r.start = lo * sz; r.count = cnt * sz;
        at.updateRanges.push(r);
        at.needsUpdate = true;
      }
      this._lo = 0; this._hi = -1;
    }
    this.geometry.instanceCount = this.wrapped ? this.max : this.head;
    this.mesh.visible = this.geometry.instanceCount > 0;
  }

  /** True unexpired occupancy; capture/debug only (the render prefix can be larger). */
  count(now) {
    let live = 0;
    const n = this.wrapped ? this.max : this.head;
    for (let i = 0; i < n; i++) if (this.expiry[i] > now) live++;
    return live;
  }

  clear() {
    this.a.fill(0); this.expiry.fill(-1); this.priority.fill(0); this.maxExpiry = -1;
    this.head = 0; this.wrapped = false; this._lo = 0; this._hi = this.max - 1;
    this.spawned = 0; this.overwrites = 0; this.dropped = 0;
  }

  dispose() { this.geometry.dispose(); this.material.dispose(); }
}
